import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RegistrationLinks } from "@/components/staff/RegistrationLinks";
import { Loader2, Users, CreditCard, FileText, ClipboardList, TrendingUp, LogOut, RefreshCw, ArrowRight } from "lucide-react";

const ProgrammePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [enrolments, setEnrolments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [discharges, setDischarges] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sRes, eRes, pRes, dRes, rRes] = await Promise.all([
        fetch(`${API}/staff/programme-stats`, { headers }),
        fetch(`${API}/staff/programme-enrolments?limit=20`, { headers }),
        fetch(`${API}/staff/payment-history?limit=20`, { headers }),
        fetch(`${API}/staff/all-discharges?limit=20`, { headers }),
        fetch(`${API}/staff/all-renewals?limit=20`, { headers }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (eRes.ok) { const d = await eRes.json(); setEnrolments(d.enrolments); }
      if (pRes.ok) { const d = await pRes.json(); setPayments(d.payments); }
      if (dRes.ok) { const d = await dRes.json(); setDischarges(d.discharges); }
      if (rRes.ok) { const d = await rRes.json(); setRenewals(d.renewals); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  const tabMap = { "Total enrolments": "overview", "Active": "overview", "Payments": "payments", "Discharges": "discharges", "Renewals": "renewals" };

  const cards = [
    { label: "Total enrolments", value: stats?.total_enrolments || 0, icon: Users, color: "#FF7A00" },
    { label: "Active", value: stats?.active_enrolments || 0, icon: TrendingUp, color: "#059669" },
    { label: "Payments", value: stats?.successful_payments || 0, icon: CreditCard, color: "#2563EB" },
    { label: "Discharges", value: discharges.length, icon: LogOut, color: "#7C3AED" },
    { label: "Renewals", value: renewals.length, icon: RefreshCw, color: "#0891B2" },
  ];

  const dischargeStatusColor = {
    initiated: "bg-yellow-50 text-yellow-700 border-yellow-200",
    consultation_complete: "bg-blue-50 text-blue-700 border-blue-200",
    discharged: "bg-green-50 text-green-700 border-green-200",
    renewed: "bg-purple-50 text-purple-700 border-purple-200",
    renewal_awaiting_payment: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div className="space-y-6" data-testid="programme-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Programme Management</h1>
        <p className="mt-1 text-base text-stone-500">Enrolments, payments, discharges and renewals</p>
      </div>

      <RegistrationLinks token={token} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map(c => (
          <Card key={c.label} className={`rounded-xl border-stone-200/60 p-4 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === c.label ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === c.label ? null : c.label); setTab(tabMap[c.label]); }}>
            <c.icon className="w-5 h-5 mb-2" style={{ color: c.color }} />
            <p className="text-2xl font-bold text-stone-900">{c.value}</p>
            <p className="text-xs text-stone-500 mt-0.5">{c.label}</p>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg">
          <TabsTrigger value="overview" className="rounded-md text-sm">Enrolments</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-md text-sm">Payments</TabsTrigger>
          <TabsTrigger value="discharges" className="rounded-md text-sm" data-testid="tab-discharges">Discharges ({discharges.length})</TabsTrigger>
          <TabsTrigger value="renewals" className="rounded-md text-sm" data-testid="tab-renewals">Renewals ({renewals.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "overview" && (
        <Card className="rounded-xl border-stone-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100"><h3 className="text-base font-bold text-stone-900">Recent enrolments</h3></div>
          {enrolments.length > 0 ? (
            <div className="divide-y divide-stone-100">
              {enrolments.map(e => (
                <div key={e.enrollment_id} className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-stone-50/50" onClick={() => navigate(`/admin/patients/${e.user_id}`)}>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{e.participant_name}</p>
                    <p className="text-xs text-stone-400">{e.participant_email} &middot; {e.enrolled_at?.split("T")[0]}</p>
                  </div>
                  <Badge variant="outline" className={`text-xs capitalize ${e.status === "active" ? "bg-green-50 text-green-700 border-green-200" : ""}`}>{e.status || "enrolled"}</Badge>
                </div>
              ))}
            </div>
          ) : <div className="p-8 text-center text-sm text-stone-400">No enrolments yet.</div>}
        </Card>
      )}

      {tab === "payments" && (
        <Card className="rounded-xl border-stone-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100"><h3 className="text-base font-bold text-stone-900">Payment history</h3></div>
          {payments.length > 0 ? (
            <div className="divide-y divide-stone-100">
              {payments.map(p => (
                <div key={p.transaction_id || p.reference} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{p.reference || p.transaction_id?.slice(0, 16)}</p>
                    <p className="text-xs text-stone-400">{p.payment_type || "programme"} &middot; {p.amount ? `${p.currency || "NGN"} ${p.amount}` : "N/A"} &middot; {p.created_at?.split("T")[0]}</p>
                  </div>
                  <Badge variant="outline" className={`text-xs capitalize ${p.payment_status === "successful" ? "bg-green-50 text-green-700 border-green-200" : p.payment_status === "failed" ? "bg-red-50 text-red-700 border-red-200" : ""}`}>{p.payment_status}</Badge>
                </div>
              ))}
            </div>
          ) : <div className="p-8 text-center text-sm text-stone-400">No payments yet.</div>}
        </Card>
      )}

      {tab === "discharges" && (
        <div className="space-y-3">
          {discharges.length > 0 ? discharges.map(d => (
            <Card key={d.discharge_id} className="rounded-xl border-stone-200/60 p-5" data-testid={`discharge-${d.discharge_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-stone-900">{d.patient_name}</p>
                  <p className="text-xs text-stone-400">{d.patient_email} &middot; Initiated: {d.created_at?.split("T")[0]}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-xs capitalize ${dischargeStatusColor[d.status] || ""}`}>{d.status?.replace(/_/g, " ")}</Badge>
                  {d.discharge_outcome && <Badge variant="outline" className="text-xs capitalize">{d.discharge_outcome}</Badge>}
                </div>
              </div>
              {d.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  <div className="bg-stone-50 rounded-lg px-3 py-2"><p className="text-lg font-bold text-stone-900">{d.stats.total_checkins_completed}</p><p className="text-xs text-stone-500">Check-ins</p></div>
                  <div className="bg-stone-50 rounded-lg px-3 py-2"><p className="text-lg font-bold text-stone-900">{d.stats.total_consultations}</p><p className="text-xs text-stone-500">Consultations</p></div>
                  <div className="bg-stone-50 rounded-lg px-3 py-2"><p className="text-lg font-bold text-stone-900">{d.stats.total_observations}</p><p className="text-xs text-stone-500">Measurements</p></div>
                  <div className="bg-stone-50 rounded-lg px-3 py-2">
                    <p className={`text-lg font-bold ${d.stats.weight_change_kg && d.stats.weight_change_kg < 0 ? "text-green-600" : "text-stone-900"}`}>
                      {d.stats.weight_change_kg !== null ? `${d.stats.weight_change_kg > 0 ? "+" : ""}${d.stats.weight_change_kg} kg` : "N/A"}
                    </p>
                    <p className="text-xs text-stone-500">Weight change</p>
                  </div>
                </div>
              )}
              {d.discharge_notes && <p className="text-sm text-stone-600 mb-2">{d.discharge_notes}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => navigate(`/admin/patients/${d.patient_id}`)}>
                  View Profile <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </Card>
          )) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <LogOut className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No discharge records yet. Discharges are initiated from individual patient profiles at Week 12.</p>
            </Card>
          )}
        </div>
      )}

      {tab === "renewals" && (
        <div className="space-y-3">
          {renewals.length > 0 ? renewals.map(r => (
            <Card key={r.renewal_id} className="rounded-xl border-stone-200/60 p-5" data-testid={`renewal-${r.renewal_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-stone-900">{r.renewal_weeks}-week renewal</p>
                  <p className="text-xs text-stone-400">
                    Patient: {r.patient_id} &middot; {r.payment_currency} {r.payment_amount} &middot;
                    Start: {r.start_date?.split("T")[0]} &middot; End: {r.end_date?.split("T")[0]}
                  </p>
                </div>
                <Badge variant="outline" className={`text-xs capitalize ${r.status === "active" ? "bg-green-50 text-green-700 border-green-200" : ""}`}>{r.status}</Badge>
              </div>
            </Card>
          )) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <RefreshCw className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No renewals yet. Renewals are created after a discharge outcome of "renewed" and a successful Paystack payment.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgrammePage;
